import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TextField from '@mui/material/TextField';
import { actionHelpers } from 'app-components/flux/core';
import { getPropertyNameFull } from 'app-components/flux/house-stats/houseStatsUtils';
import { housingSelectionActions } from 'app-components/flux/housing-selection';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import type { AutocompleteProps } from '@mui/material/Autocomplete';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import type { HousingSelectionType } from 'app-components/flux/housing-selection/housingSelectionTypes';
import { mkHousing } from 'app-components/flux/housing-selection/housingSelectionTypes';
import { tracker } from 'app-components/utils/core';
import useDebouncedCallback from 'use-debounce/lib/useDebouncedCallback';

const useStyles = makeStyles()((theme) => ({
  popperShadow: {
    boxShadow: theme.shadows[6],
    borderRadius: 12,
  },
  minWidth: {
    minWidth: 200,
  },
}));

type AddressSearchFieldProps = Omit<
  AutocompleteProps<HousingSelectionType, false, true, false>,
  'renderInput' | 'options'
> & {
  allowManual?: boolean;
};

const getOptionLabel = (option: HousingSelectionType) => {
  if (option.id === 'Create manually') {
    return `Add "${option.line1?.trim()}" manually`;
  }
  return getPropertyNameFull(option) || 'Unknown';
};

export const addManualAddressOption = (
  options: HousingSelectionType[],
  inputValue: string,
  allowManual: boolean,
): HousingSelectionType[] => {
  if (!allowManual || inputValue === '') {
    return options;
  }

  return [
    ...options,
    mkHousing({
      id: 'Create manually',
      line1: inputValue,
    }),
  ];
};

const AddressSearchField = (props: AddressSearchFieldProps) => {
  const dispatch = useDispatch();
  const { allowManual = true, onChange, ...other } = props;
  const { classes } = useStyles();
  const [loading, setLoading] = useState(false);
  const [houseOptions, setHouseOptions] = useState<HousingSelectionType[] | null>();

  const [debounceAutocomplete] = useDebouncedCallback(
    (searchVal: string) =>
      actionHelpers
        .bindPromiseAction<string, any, HousingSelectionType[]>(
          dispatch,
          housingSelectionActions.getAutocompleteHousingSelection,
        )(searchVal)
        .then((response) => {
          tracker.track(tracker.events.zillowUX_2, { propertySearchResults: response.length });
          if (response.length) {
            setHouseOptions(
              response.filter(
                (zillowResults) =>
                  zillowResults.assetDetailsList && zillowResults.assetDetailsList[0]?.thirdPartyPropertyID,
              ),
            );
          } else {
            setHouseOptions(null);
          }
          setLoading(false);
        })
        .catch(() => {
          tracker.track(tracker.events.zillowUX_2, { propertySearchResults: 'FAILED' });
          setHouseOptions(null);
          setLoading(false);
        }),
    500,
  );

  return (
    <Autocomplete
      id='address-field'
      onChange={(e, value, _reason, _details) => {
        onChange?.(e, value, _reason, _details);
        setHouseOptions(null);
      }}
      classes={{ root: classes.minWidth, paper: classes.popperShadow }}
      onBlur={() => setHouseOptions(null)}
      onInputChange={(e, value, details) => {
        if (details !== 'reset') {
          setLoading(true);
          debounceAutocomplete(value);
        }
      }}
      loading={loading}
      disableClearable
      clearOnBlur
      noOptionsText='No address found'
      filterOptions={(options, params) => {
        if (loading) {
          return [];
        }
        const { inputValue } = params;
        return addManualAddressOption(options, inputValue, allowManual);
      }}
      options={houseOptions || []}
      popupIcon={loading ? <CircularProgress color='inherit' size={20} /> : <ArrowDropDownIcon />}
      renderOption={(propsArg, option) => {
        const label = getOptionLabel(option);
        const isAddManual = option.id === 'Create manually';
        const manualStyling = {
          sx: {
            color: 'primary.main',
            textTransform: 'none',
            alignItems: 'center',
            display: 'flex',
            gap: 1,
          },
        };
        return (
          <ListItem {...propsArg} key={option.id}>
            <Typography variant={'bodyRegular'} {...(isAddManual ? manualStyling : null)}>
              {isAddManual ? <AddCircleIcon /> : null}
              {label}
            </Typography>
          </ListItem>
        );
      }}
      {...other}
      renderInput={(params) => {
        return <TextField {...params} label='Address' />;
      }}
    />
  );
};

export default AddressSearchField;
